import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import {
  DecaStopType,
  IDecaDocument,
  IDecaDocumentSegment,
  isDecaServicePriorToTimestamp,
  IStopAddress,
  UUID,
} from '@okcargo-apps/shared-domain';
import { IPlace } from '@okcargo-apps/shared-domain-ionic';
import { SearchAddressbookModalComponent, SearchCityPostalcodeModalComponent } from '../../freight-shipper';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaEditAddressForm, DecaEditReasonFormModel, IStopLocationData } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-address-modal',
  templateUrl: './deca-edit-address-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonInput,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    TranslateModule,
    DecaEditReasonComponent,
    DecaListItemComponent,
  ],
})
export class DecaEditAddressModalComponent implements OnInit {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;
  public readonly stopType!: DecaStopType;
  public readonly segment!: IDecaDocumentSegment;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);

  protected readonly form: FormGroup<DecaEditAddressForm> = this.fb.group<DecaEditAddressForm>({
    companyName: this.fb.nonNullable.control('', Validators.required),
    street: this.fb.nonNullable.control('', Validators.required),
    changeReason: this.fb.group<DecaEditReasonFormModel>({ reason: this.fb.nonNullable.control('') }),
  });

  protected get isServiceStarted(): boolean {
    return isDecaServicePriorToTimestamp(this.document, new Date());
  }

  protected readonly locationData = signal<IStopLocationData | null>(null);
  protected readonly showLocationError = signal<boolean>(false);

  private requireChangeReasonWhenServiceStarted(): void {
    if (!this.isServiceStarted) return;

    const reason = this.form.controls.changeReason.controls.reason;
    reason.addValidators(Validators.required);
    reason.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.requireChangeReasonWhenServiceStarted();
    const stopData = this.segment[this.stopType];

    this.form.patchValue({
      companyName: stopData.companyName ?? '',
      street: stopData.street ?? '',
    });

    if (stopData.city && stopData.postalCode) {
      this.locationData.set({
        city: stopData.city,
        postalCode: stopData.postalCode,
        stateCode: stopData.stateCode,
        countryCode: stopData.countryCode,
      });
    }
  }

  protected get modalTitle(): string {
    return this.stopType === 'origin' ? 'Editar dirección de origen' : 'Editar dirección de destino';
  }

  public async openAddressbookModal(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: SearchAddressbookModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<IStopAddress>();

    if (role === 'confirm' && data) {
      this.form.patchValue({
        companyName: data.company,
        street: data.street,
      });

      this.locationData.set({
        city: data.city,
        postalCode: data.postalCode,
        stateCode: data.stateCode,
        countryCode: data.countryCode,
      });
      this.showLocationError.set(false);
    }
  }

  public async openPostalCodeModal(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: SearchCityPostalcodeModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<IPlace>();

    if (role === 'confirm' && data) {
      this.locationData.set({
        city: data.city,
        postalCode: data.postalCode,
        stateCode: data.stateCode,
        countryCode: data.countryCode,
      });
      this.showLocationError.set(false);
    }
  }

  protected async saveChanges(): Promise<void> {
    const location = this.locationData();
    const hasLocation = location !== null;

    if (!hasLocation) {
      this.showLocationError.set(true);
    }

    if (this.form.invalid || !hasLocation) {
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const formValues = this.form.getRawValue();

      const updateSegment: IDecaDocumentSegment = {
        ...this.segment,
        [this.stopType]: {
          ...this.segment[this.stopType],
          companyName: formValues.companyName,
          street: formValues.street,
          city: location.city,
          postalCode: location.postalCode,
          stateCode: location.stateCode,
          countryCode: location.countryCode,
        },
      };

      const updatedDocument: IDecaDocument = {
        ...this.document,
        segments: this.document.segments.map((segment) => (segment.id === updateSegment.id ? updateSegment : segment)),
      };

      await this.decaMutationsService.updateStopAddress(
        updatedDocument,
        this.stopType,
        this.segment.id,
        this.form.controls.changeReason.controls.reason.value.trim(),
      );

      const label = this.stopType === 'origin' ? 'origen' : 'destino';
      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: [`Ha enviado una nueva dirección de ${label} a este documento`],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error(`Error updating ${this.stopType} address`, error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
